export interface AppConfig {
  /** Empty means "same origin", which is what the dev server does: it proxies /api and /r to the API. */
  apiBaseUrl: string;
  tenantId: string;
  clientId: string;
  /** Scope exposed by the API registration, e.g. api://<api-client-id>/access_as_user */
  apiScope: string;
  redirectUri: string;
  /** Algolia application holding the public product index. Empty disables the product code type. */
  productSearchAppId: string;
  /** Search-only key, scoped to the product index alone. Never the whole-catalogue key. */
  productSearchApiKey: string;
  productSearchIndexName: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<AppConfig>;
  }
}

const runtime: Partial<AppConfig> = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};

function value(fromRuntime: string | undefined, fromBuild: string | undefined, fallback = ''): string {
  const candidate = fromRuntime?.trim() || fromBuild?.trim() || fallback;
  return candidate;
}

/**
 * Runtime configuration wins over build-time: the deploy workflow rewrites /config.js with the target
 * environment's values, so one build can be promoted between environments. In development nothing rewrites it,
 * its placeholders are discarded, and the VITE_* variables are used instead.
 */
export const appConfig: AppConfig = {
  apiBaseUrl: value(runtime.apiBaseUrl, import.meta.env.VITE_API_BASE_URL),
  tenantId: value(runtime.tenantId, import.meta.env.VITE_AZURE_TENANT_ID),
  clientId: value(runtime.clientId, import.meta.env.VITE_AZURE_CLIENT_ID),
  apiScope: value(runtime.apiScope, import.meta.env.VITE_API_SCOPE),
  redirectUri: value(
    runtime.redirectUri,
    import.meta.env.VITE_REDIRECT_URI,
    typeof window !== 'undefined' ? window.location.origin : '',
  ),
  productSearchAppId: value(runtime.productSearchAppId, import.meta.env.VITE_PRODUCT_SEARCH_APP_ID),
  productSearchApiKey: value(runtime.productSearchApiKey, import.meta.env.VITE_PRODUCT_SEARCH_API_KEY),
  productSearchIndexName: value(
    runtime.productSearchIndexName,
    import.meta.env.VITE_PRODUCT_SEARCH_INDEX_NAME,
    'fds_products',
  ),
};

export function productSearchConfigured(config: AppConfig = appConfig): boolean {
  return Boolean(config.productSearchAppId && config.productSearchApiKey && config.productSearchIndexName);
}

/** Human-readable list of what is missing, so a misconfigured deployment says so instead of failing silently. */
export function missingConfigKeys(config: AppConfig = appConfig): string[] {
  const required: Array<[keyof AppConfig, string]> = [
    ['tenantId', 'VITE_AZURE_TENANT_ID / tenantId'],
    ['clientId', 'VITE_AZURE_CLIENT_ID / clientId'],
    ['apiScope', 'VITE_API_SCOPE / apiScope'],
  ];

  return required.filter(([key]) => !config[key]).map(([, label]) => label);
}
